import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  analyzeCareerResilience,
} from "../api/careerResilienceApi";
import "../styles/career-resilience-audit.css";
import AppFooter from "../components/AppFooter";
import logo from "../assets/logo_basic.png";

const initialForm = {
  currentRole: "",
  dailyTasks: "",
  challengesSolved: "",
  humanInteraction: "",
  decisionMakingResponsibilities: "",
  toolsAndTechnologies: "",
  yearsOfExperience: "",
  age: "",
  industry: "",
  country: "India",
  careerGoal: "",
  existingSkills: "",
  learningPreferences: "",
};

export default function CareerResilienceAudit() {
  const navigate = useNavigate();

  const [form, setForm] = useState(initialForm);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const updateField = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const validate = () => {
    if (!form.currentRole.trim()) {
      return "Please enter your current role.";
    }

    if (form.dailyTasks.trim().length < 10) {
      return "Please describe your daily tasks in more detail.";
    }

    const experience = Number(
      form.yearsOfExperience
    );

    if (
      Number.isNaN(experience) ||
      experience < 0 ||
      experience > 60
    ) {
      return "Enter valid years of experience between 0 and 60.";
    }

    if (form.age) {
      const age = Number(form.age);

      if (
        Number.isNaN(age) ||
        age < 16 ||
        age > 100
      ) {
        return "Enter a valid age between 16 and 100.";
      }
    }

    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationError = validate();

    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      const payload = {
        currentRole: form.currentRole.trim(),
        dailyTasks: form.dailyTasks.trim(),
        challengesSolved:
          form.challengesSolved.trim(),
        humanInteraction:
          form.humanInteraction.trim(),
        decisionMakingResponsibilities:
          form.decisionMakingResponsibilities.trim(),
        toolsAndTechnologies:
          form.toolsAndTechnologies.trim(),
        yearsOfExperience: Number(
          form.yearsOfExperience
        ),
        age:
          form.age === ""
            ? null
            : Number(form.age),
        industry: form.industry.trim(),
        country: form.country.trim(),
        careerGoal: form.careerGoal.trim(),
        existingSkills:
          form.existingSkills.trim(),
        learningPreferences:
          form.learningPreferences.trim(),
      };

      const response =
        await analyzeCareerResilience(
          payload
        );

      setResult(response);

      window.setTimeout(() => {
        document
          .getElementById(
            "career-resilience-result"
          )
          ?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
      }, 100);
    } catch (requestError) {
      setError(
        requestError?.message ||
          "Unable to complete the analysis."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cra-shell">
      <header className="cra-header">
        <div className="cra-header-inner">
          <div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="cra-back-button"
            >
              ← Back
            </button>

            <h1 className="cra-page-title">
              AI Career{" "}
              <span>Risk Calculator</span>
            </h1>

            <div className="cra-title-line" />
          </div>

          <button
            type="button"
            onClick={() =>
              navigate("/dashboard")
            }
            className="cra-dashboard-button"
          >
            Dashboard
          </button>
        </div>
      </header>

      <main className="cra-main">
        <div className="cra-container">
          <section className="cra-intro">
            <p className="cra-eyebrow">
              Reignite Spirit Free Tool
            </p>

            <h2>
              Understand how AI may transform
              your current role
            </h2>

            <p>
              Describe your work,
              responsibilities and skills to
              receive an AI-risk assessment,
              resilience score and practical
              career recommendations.
            </p>
          </section>

          <form
            className="cra-card"
            onSubmit={handleSubmit}
          >
            <div className="cra-form-section">
              <div className="cra-section-heading">
                <span>1</span>

                <div>
                  <h3>
                    Current Career Profile
                  </h3>

                  <p>
                    Tell us about your present
                    role and professional
                    background.
                  </p>
                </div>
              </div>

              <FormField
                label="Current role"
                name="currentRole"
                value={form.currentRole}
                onChange={updateField}
                placeholder="Example: Senior Customer Support Executive"
                required
              />

              <div className="cra-two-column">
                <FormField
                  label="Industry"
                  name="industry"
                  value={form.industry}
                  onChange={updateField}
                  placeholder="Example: Software Services"
                />

                <FormField
                  label="Country"
                  name="country"
                  value={form.country}
                  onChange={updateField}
                  placeholder="Example: India"
                />

                <FormField
                  label="Years of experience"
                  name="yearsOfExperience"
                  type="number"
                  min="0"
                  max="60"
                  step="0.5"
                  value={
                    form.yearsOfExperience
                  }
                  onChange={updateField}
                  placeholder="8"
                  required
                />

                <FormField
                  label="Age"
                  name="age"
                  type="number"
                  min="16"
                  max="100"
                  value={form.age}
                  onChange={updateField}
                  placeholder="34"
                />
              </div>
            </div>

            <div className="cra-form-section">
              <div className="cra-section-heading">
                <span>2</span>

                <div>
                  <h3>
                    Daily Work and
                    Responsibilities
                  </h3>

                  <p>
                    Explain the actual tasks,
                    decisions and challenges in
                    your role.
                  </p>
                </div>
              </div>

              <TextAreaField
                label="Typical daily tasks"
                name="dailyTasks"
                value={form.dailyTasks}
                onChange={updateField}
                placeholder="Describe the main tasks you perform during a normal working day..."
                required
              />

              <TextAreaField
                label="Complex challenges you solve"
                name="challengesSolved"
                value={
                  form.challengesSolved
                }
                onChange={updateField}
                placeholder="Describe problems that require judgment, analysis or creative thinking..."
              />

              <TextAreaField
                label="Decision-making responsibilities"
                name="decisionMakingResponsibilities"
                value={
                  form.decisionMakingResponsibilities
                }
                onChange={updateField}
                placeholder="Describe decisions you make independently or recommend to others..."
              />
            </div>

            <div className="cra-form-section">
              <div className="cra-section-heading">
                <span>3</span>

                <div>
                  <h3>
                    Human Advantage and
                    Technology
                  </h3>

                  <p>
                    Help us understand the human
                    interaction and technical
                    tools involved in your work.
                  </p>
                </div>
              </div>

              <TextAreaField
                label="Human interaction required"
                name="humanInteraction"
                value={
                  form.humanInteraction
                }
                onChange={updateField}
                placeholder="Describe customer interaction, leadership, negotiation, empathy or teamwork..."
              />

              <TextAreaField
                label="Tools and technologies"
                name="toolsAndTechnologies"
                value={
                  form.toolsAndTechnologies
                }
                onChange={updateField}
                placeholder="Example: CRM, Excel, ticketing platforms, reporting tools..."
              />
            </div>

            <div className="cra-form-section">
              <div className="cra-section-heading">
                <span>4</span>

                <div>
                  <h3>
                    Career Development
                  </h3>

                  <p>
                    Tell us where you want to go
                    and how you prefer to learn.
                  </p>
                </div>
              </div>

              <TextAreaField
                label="Existing skills"
                name="existingSkills"
                value={
                  form.existingSkills
                }
                onChange={updateField}
                placeholder="List your strongest technical, professional and human skills..."
              />

              <FormField
                label="Career goal"
                name="careerGoal"
                value={form.careerGoal}
                onChange={updateField}
                placeholder="Example: Move into customer success or AI-enabled operations"
              />

              <TextAreaField
                label="Learning preferences"
                name="learningPreferences"
                value={
                  form.learningPreferences
                }
                onChange={updateField}
                placeholder="Example: Online courses, practical projects and weekend study"
              />
            </div>

            {error && (
              <div
                className="cra-error"
                role="alert"
              >
                <strong>
                  Unable to generate analysis
                </strong>

                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="cra-submit-button"
            >
              {loading ? (
                <>
                  <span className="cra-spinner" />
                  Analysing your career...
                </>
              ) : (
                <>
                  Generate My AI Risk
                  Assessment
                  <span>→</span>
                </>
              )}
            </button>

            <div className="cra-disclaimer">
              <strong>Important:</strong>{" "}
              This tool provides career guidance
              based on the information entered.
              It is not a guaranteed prediction
              of job loss, future hiring demand
              or employment outcomes.
            </div>
          </form>

          {result && (
            <CareerResilienceResult
              result={result}
            />
          )}
        </div>

        <AppFooter
          variant="landing"
          logoSrc={logo}
        />
      </main>
    </div>
  );
}

function FormField({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
  ...rest
}) {
  return (
    <div className="cra-form-group">
      <label htmlFor={name}>
        {label}

        {required && (
          <span className="cra-required">
            *
          </span>
        )}
      </label>

      <input
        id={name}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        {...rest}
      />
    </div>
  );
}

function TextAreaField({
  label,
  name,
  value,
  onChange,
  required = false,
  placeholder = "",
}) {
  return (
    <div className="cra-form-group">
      <label htmlFor={name}>
        {label}

        {required && (
          <span className="cra-required">
            *
          </span>
        )}
      </label>

      <textarea
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        rows={5}
        placeholder={placeholder}
      />
    </div>
  );
}

function CareerResilienceResult({
  result,
}) {
  return (
    <section
      id="career-resilience-result"
      className="cra-result"
    >
      <header className="cra-result-header">
        <div>
          <p>
            Career Resilience Assessment
          </p>

          <h2>
            {result.riskCategory ||
              "Assessment Complete"}
          </h2>
        </div>

        <div className="cra-main-score">
          <strong>
            {result.overallRiskScore ?? 0}
          </strong>

          <span>
            AI Risk Score / 100
          </span>
        </div>
      </header>

      <div className="cra-score-grid">
        <ScoreCard
          title="Automation Exposure"
          score={
            result.automationExposureScore
          }
        />

        <ScoreCard
          title="Human Advantage"
          score={
            result.humanAdvantageScore
          }
        />

        <ScoreCard
          title="Future Demand"
          score={
            result.futureDemandScore
          }
        />

        <ScoreCard
          title="Career Resilience"
          score={
            result.careerResilienceScore
          }
        />

        <ScoreCard
          title="Skill Transferability"
          score={
            result.skillTransferabilityScore
          }
        />

        <ScoreCard
          title="Adaptability"
          score={result.adaptabilityScore}
        />
      </div>

      <ReportSection title="Executive Summary">
        <p>
          {result.executiveSummary ||
            "No summary was returned."}
        </p>
      </ReportSection>

      <ReportSection title="Risk Explanation">
        <p>
          {result.riskExplanation ||
            "No explanation was returned."}
        </p>
      </ReportSection>

      <StringList
        title="Tasks with Higher Automation Exposure"
        items={
          result.tasksAtHighRisk
        }
      />

      <StringList
        title="Tasks Likely to Be AI-Assisted"
        items={
          result.tasksAtModerateRisk
        }
      />

      <StringList
        title="Human-Led and Lower-Risk Tasks"
        items={
          result.tasksAtLowRisk
        }
      />

      <StringList
        title="Your Human Advantages"
        items={
          result.humanAdvantages
        }
      />

      <StringList
        title="Transferable Skills"
        items={
          result.transferableSkills
        }
      />

      <ReportSection title="Recommended Skills">
        <div className="cra-recommendation-grid">
          {result.recommendedSkills?.map(
            (item, index) => (
              <article
                key={`${item.skill}-${index}`}
                className="cra-recommendation-card"
              >
                <span>
                  {item.priority ||
                    "Recommended"}{" "}
                  Priority
                </span>

                <h3>
                  {item.skill}
                </h3>

                <p>
                  {item.reason}
                </p>

                <strong>
                  Suggested learning path
                </strong>

                <p>
                  {
                    item.suggestedLearningPath
                  }
                </p>
              </article>
            )
          )}
        </div>
      </ReportSection>

      <ReportSection title="Recommended Career Pathways">
        <div className="cra-recommendation-grid">
          {result.recommendedCareers?.map(
            (career, index) => (
              <article
                key={`${career.career}-${index}`}
                className="cra-recommendation-card"
              >
                <span>
                  {career.matchScore ?? 0}%
                  Match
                </span>

                <h3>
                  {career.career}
                </h3>

                <p>
                  {career.whyItFits}
                </p>

                <StringList
                  title="Skill gaps"
                  items={
                    career.skillGaps
                  }
                />
              </article>
            )
          )}
        </div>
      </ReportSection>

      <ReportSection title="Recommended Next Steps">
        <ol>
          {result.nextSteps?.map(
            (step, index) => (
              <li
                key={
                  step.order ??
                  index
                }
              >
                <strong>
                  {step.action}
                </strong>

                <p>
                  Timeframe:{" "}
                  {step.timeframe}
                </p>

                <p>
                  Expected outcome:{" "}
                  {
                    step.expectedOutcome
                  }
                </p>
              </li>
            )
          )}
        </ol>
      </ReportSection>

      <StringList
        title="90-Day Action Plan"
        items={
          result.ninetyDayPlan
        }
        ordered
      />

      <p className="cra-result-disclaimer">
        {result.disclaimer ||
          "This assessment is intended for career guidance only and does not guarantee employment outcomes."}
      </p>
    </section>
  );
}

function ScoreCard({
  title,
  score,
}) {
  return (
    <article className="cra-score-card">
      <span>{title}</span>

      <strong>
        {score ?? 0}
      </strong>

      <small>
        out of 100
      </small>
    </article>
  );
}

function ReportSection({
  title,
  children,
}) {
  return (
    <section className="cra-report-section">
      <h2>{title}</h2>

      {children}
    </section>
  );
}

function StringList({
  title,
  items = [],
  ordered = false,
}) {
  if (!Array.isArray(items) || !items.length) {
    return null;
  }

  const ListTag =
    ordered ? "ol" : "ul";

  return (
    <section className="cra-report-section">
      <h2>{title}</h2>

      <ListTag>
        {items.map(
          (item, index) => (
            <li
              key={`${item}-${index}`}
            >
              {item}
            </li>
          )
        )}
      </ListTag>
    </section>
  );
}